import { createContext, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext(null);

const THEME_STORAGE_KEY = "clyntr_theme_v2";
const THEMES = ["light", "dark", "system"];
const THEME_CLASSES = ["dark"]; // classes that get toggled on <html>

function getSystemTheme() {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "system";
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    // Migrate old theme values to new ones
    if (saved === "ember" || saved === "void") return "light";
    return THEMES.includes(saved) ? saved : "light";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const root = document.documentElement;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const resolved = theme === "system" ? getSystemTheme() : theme;
      // Remove all theme classes first
      THEME_CLASSES.forEach((cls) => root.classList.remove(cls));
      // Add the resolved class (light has no class)
      if (resolved !== "light") {
        root.classList.add(resolved);
      }
    };

    apply();
    localStorage.setItem(THEME_STORAGE_KEY, theme);

    if (theme === "system") {
      media.addEventListener("change", apply);
      return () => media.removeEventListener("change", apply);
    }
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      themes: THEMES
    }),
    [theme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}
